import { useEffect, useRef } from 'react';
import { Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRouter } from './router';
import { useAuthStore } from '@store/authStore';
import { ThemeProvider } from '@components/providers/ThemeProvider';
import { PWAInstallPrompt } from '@/components/pwa/PWAInstallPrompt';
import { OfflineIndicator } from '@/components/pwa/OfflineIndicator';
import { OnboardingProvider, OnboardingOverlay } from '@/components/onboarding';
import { CookieConsentBanner } from '@/components/privacy/CookieConsentBanner';
import { SessionExpiredModal } from '@/components/auth/SessionExpiredModal';
import { ChunkLoadErrorBoundary } from '@/components/error/ChunkLoadErrorBoundary';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { useSessionCheck } from '@/hooks/useSessionCheck';
import { apiClient } from '@/api/client';
import i18n from 'i18next';
import './lib/i18n';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// AppContent component moved outside App to prevent recreation on every render
const AppContent = () => (
  <>
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: 'var(--toast-bg, white)',
          color: 'var(--toast-color, #1f2937)',
          border: '1px solid var(--toast-border, #e5e7eb)',
        },
        classNames: {
          success: 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
          error: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
          warning: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
          info: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
        },
      }}
    />
    <OnboardingProvider>
      <AppRouter />
      <OnboardingOverlay />
    </OnboardingProvider>
    <OfflineIndicator />
    <PWAInstallPrompt />
    <SessionExpiredModal />
    <CookieConsentBanner />
  </>
);

function App() {
  const { setLoading, isAuthenticated } = useAuthStore();
  const languageLoadedRef = useRef(false);

  // Proactively check session on app focus/visibility change
  useSessionCheck();

  // Initialize auth state on app load
  useEffect(() => {
    const initAuth = async () => {
      // Check if we have tokens in localStorage
      const storedToken = localStorage.getItem('accessToken');

      if (storedToken && !isAuthenticated) {
        // Token exists but store is not authenticated - restore from localStorage
        // The persist middleware should handle this, but just in case
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    initAuth();
  }, [setLoading, isAuthenticated]);

  // Load user's language preference from backend when authenticated
  // Only as fallback - localStorage (tremoria-language) is the primary source
  useEffect(() => {
    const loadUserLanguage = async () => {
      // Prevent duplicate calls in React Strict Mode
      if (!isAuthenticated || languageLoadedRef.current) return;

      // Also check if we actually have a token (persisted state might be stale)
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return;
      }

      // If user has explicitly set a language in localStorage, respect that
      const localStorageLanguage = localStorage.getItem('tremoria-language');
      if (localStorageLanguage) {
        // Language is already set from localStorage by i18next-browser-languagedetector
        languageLoadedRef.current = true;
        return;
      }

      languageLoadedRef.current = true;

      try {
        const response = await apiClient.get<{ profile: { language?: string; timezone?: string } }>('/api/v1/me/profile');
        const { language } = response.profile || {};

        // Apply language from backend only if no local preference exists
        if (language && i18n.language !== language) {
          await i18n.changeLanguage(language);
        }
      } catch (err) {
        // Silently fail - user will see default language
        console.debug('Could not load user language preference:', err);
        // Don't retry on auth errors - the token is invalid
        const error = err as { statusCode?: number };
        if (error.statusCode !== 401) {
          languageLoadedRef.current = false;
        }
      }
    };

    loadUserLanguage();
  }, [isAuthenticated]);

  return (
    <ChunkLoadErrorBoundary>
      <ErrorBoundary>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AppContent />
          </QueryClientProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </ChunkLoadErrorBoundary>
  );
}

export default App;
